import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CheckCircle, AlertCircle, Clock, Shield, FileText, Droplets, Award, TrendingUp } from 'lucide-react';
import './ARTInteligente.css';
import useScrollReveal, { srPresets } from '../hooks/useScrollReveal';

/** Constante de módulo: a lista é lida uma única vez, na montagem. */
const REVEALS = [
    // Hero
    { selector: '.art-hero-content', options: srPresets.fadeUp },

    // Legislação
    { selector: '.legislation-section .section-header', options: srPresets.fadeUp },
    { selector: '.legislation-intro', options: srPresets.fadeUp },
    { selector: '.legislation-card', options: srPresets.fadeUp, interval: 110 },

    // O que entrega
    { selector: '.delivers-section .section-header', options: srPresets.fadeUp },
    { selector: '.deliver-card', options: srPresets.fadeUp, interval: 110 },
    { selector: '.delivers-banner', options: srPresets.zoomIn },

    // Incluído
    { selector: '.included-section .section-header', options: srPresets.fadeUp },
    { selector: '.included-item', options: srPresets.fadeRight, interval: 100 },

    // Comparativo
    { selector: '.comparison-section h2', options: srPresets.fadeUp },
    { selector: '.comparison-subtitle', options: srPresets.fadeUp },
    { selector: '.comparison-header', options: srPresets.fadeUp },
    { selector: '.comparison-row', options: srPresets.fadeUp, interval: 80 },
    { selector: '.comparison-footer', options: srPresets.zoomIn },

    // Planos
    { selector: '.plans-section h2', options: srPresets.fadeUp },
    { selector: '.plans-subtitle', options: srPresets.fadeUp },
    { selector: '.plan-card', options: srPresets.fadeUp, interval: 130 },
    { selector: '.plans-note', options: srPresets.fadeUp },

    // CTA
    { selector: '.cta-content', options: srPresets.zoomIn },
];

const ARTInteligente = () => {
  useScrollReveal(REVEALS);

  return (
    <div className="art-page">
      <Header />
      
      {/* Hero Section */}
      <section className="art-hero">
        <div className="container">
          <div className="art-hero-content">
            <h1>ART Inteligente</h1>
            <p className="art-subtitle">Responsabilidade Técnica com Monitoramento 24/7 e Formação dos "Ninjas da Piscina"</p>
          </div>
        </div>
      </section>

      {/* What Legislation Requires */}
      <section className="legislation-section">
        <div className="container">
          <div className="section-header">
            <h2>Resolução 335/23</h2>
            <h3>O que a legislação exige:</h3>
          </div>
          <p className="legislation-intro">A legislação exige responsável técnico com emissão de ART para piscinas de uso coletivo. Isso significa que no cenário atual há:</p>
          
          <div className="legislation-grid">
            <div className="legislation-card">
              <div className="card-icon red">
                <AlertCircle size={32} />
              </div>
              <h4>Insegurança jurídica</h4>
              <p>Para os responsáveis técnicos</p>
            </div>
            
            <div className="legislation-card">
              <div className="card-icon orange">
                <FileText size={32} />
              </div>
              <h4>Falta de padronização das análises de água</h4>
              <p>Cada responsável executa da forma que acha melhor</p>
            </div>
            
            <div className="legislation-card">
              <div className="card-icon yellow">
                <Shield size={32} />
              </div>
              <h4>Ausência de transparência para os banhistas</h4>
              <p>Banhistas sem acesso elevado sobre a água</p>
            </div>
            
            <div className="legislation-card">
              <div className="card-icon orange">
                <TrendingUp size={32} />
              </div>
              <h4>Dados técnicos desorganizados e não armazenados</h4>
              <p>Blocos operacionais preenchidos e descartados com informações soltas</p>
            </div>
          </div>
        </div>
      </section>

      {/* What iPoolCare Delivers */}
      <section className="delivers-section">
        <div className="container">
          <div className="section-header">
            <h2>Sobre Nós</h2>
            <h3>O Que a iPoolCare entrega</h3>
          </div>
          
          <div className="delivers-grid">
            <div className="deliver-card">
              <div className="deliver-icon">
                <Shield size={40} />
              </div>
              <h4>Responsabilidade Técnica</h4>
              <p>Profissional qualificado responsável pela piscina</p>
            </div>
            
            <div className="deliver-card">
              <div className="deliver-icon">
                <CheckCircle size={40} />
              </div>
              <h4>Supervisão Técnica Periódica</h4>
              <p>Fiscalização, controle e medidas corretivas</p>
            </div>
            
            <div className="deliver-card">
              <div className="deliver-icon">
                <Droplets size={40} />
              </div>
              <h4>Análises de Água</h4>
              <p>Gestão técnica contínua verificando todos os parâmetros</p>
            </div>
            
            <div className="deliver-card">
              <div className="deliver-icon">
                <Clock size={40} />
              </div>
              <h4>Integração</h4>
              <p>Monitoramento remoto 24/7 atuando nas anomalias</p>
            </div>
          </div>
          
          <div className="delivers-banner">
            <p>Transformando soluções em <strong>Gestão Técnica Inteligente</strong> com responsabilidade formal e infraestrutura completa.</p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="included-section">
        <div className="container">
          <div className="section-header">
            <h2>Proteção jurídica/sanitária e prevenção sempre</h2>
            <h3>O Que Está Incluído</h3>
          </div>
          
          <div className="included-grid">
            <div className="included-item">
              <div className="item-number">1</div>
              <div className="item-content">
                <h4>Responsabilidade Técnica Formal</h4>
                <ul>
                  <li>Profissional técnico habilitado</li>
                  <li>Emissão da ART conforme resolução 335/23</li>
                  <li>Registro nos órgãos competentes</li>
                </ul>
              </div>
            </div>
            
            <div className="included-item">
              <div className="item-number">2</div>
              <div className="item-content">
                <h4>Monitoramento Remoto 24/7</h4>
                <ul>
                  <li>Vigilância contínua dos parâmetros</li>
                  <li>Alertas automáticos de não conformidades</li>
                  <li>Histórico completo e rastreável</li>
                  <li>Plataforma web de acesso aos dados</li>
                </ul>
              </div>
            </div>
            
            <div className="included-item">
              <div className="item-number">3</div>
              <div className="item-content">
                <h4>Gestão Técnica Contínua</h4>
                <ul>
                  <li>Análise constante da água</li>
                  <li>Ajuste e balanceamento químico</li>
                  <li>Controle de dosagens automatizado</li>
                  <li>Relatórios técnicos periódicos</li>
                </ul>
              </div>
            </div>
            
            <div className="included-item">
              <div className="item-number">4</div>
              <div className="item-content">
                <h4>Transparência Total</h4>
                <ul>
                  <li>Acesso aos dados pelos usuários</li>
                  <li>Monitores na área da piscina</li>
                  <li>Informações em tempo real</li>
                  <li>Compliance com vigilância sanitária</li>
                </ul>
              </div>
            </div>
            
            <div className="included-item">
              <div className="item-number">5</div>
              <div className="item-content">
                <h4>Formação dos "Ninjas da Piscina"</h4>
                <ul>
                  <li>Capacitação completa da equipe</li>
                  <li>Treinamento em manutenção e limpeza</li>
                  <li>Certificação dos profissionais</li>
                  <li>Suporte técnico contínuo</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="comparison-section">
        <div className="container">
          <h2>COMPARATIVO ESTRATÉGICO</h2>
          <p className="comparison-subtitle">ART Tradicional vs iPoolCare ART Inteligente</p>
          
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="header-item traditional">
                <h3>ART Tradicional</h3>
              </div>
              <div className="header-item intelligent">
                <h3>iPoolCare ART Inteligente</h3>
              </div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-cell traditional">
                <p><strong>Responsável técnico:</strong> Documento anual</p>
              </div>
              <div className="comparison-cell intelligent">
                <p><strong>Responsabilidade técnica contínua:</strong> Gestão técnica contínua</p>
              </div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-cell traditional">
                <p><strong>Supervisão:</strong> Esporádica/passiva</p>
              </div>
              <div className="comparison-cell intelligent">
                <p><strong>Monitoramento:</strong> 24/7</p>
              </div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-cell traditional">
                <p><strong>Análises:</strong> Sem supervisão remota</p>
              </div>
              <div className="comparison-cell intelligent">
                <p><strong>Tecnologia IoT:</strong> Exclusiva</p>
              </div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-cell traditional">
                <p><strong>Treinamento:</strong> Básico operacional</p>
              </div>
              <div className="comparison-cell intelligent">
                <p><strong>Formação dos "Ninjas":</strong> Treinamento técnico completo</p>
              </div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-cell traditional">
                <p><strong>Relatórios:</strong> Manuais</p>
              </div>
              <div className="comparison-cell intelligent">
                <p><strong>Relatórios automáticos:</strong> Dados em tempo real</p>
              </div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-cell traditional">
                <p><strong>Sem supervisão remota</strong></p>
              </div>
              <div className="comparison-cell intelligent">
                <p><strong>Preço acessível:</strong> Valor real</p>
              </div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-cell traditional">
                <p><strong>Não operacional elevada</strong></p>
              </div>
              <div className="comparison-cell intelligent">
                <p><strong>Preço unitário:</strong></p>
              </div>
            </div>
          </div>
          
          <div className="comparison-footer">
            <p><strong>A iPoolCare ART Inteligente protege juridicamente, sanitariamente e preventivamente.</strong></p>
          </div>
        </div>
      </section>

      {/* Requirements and Plans */}
      <section className="plans-section">
        <div className="container">
          <h2>Requisitos e Planos</h2>
          <p className="plans-subtitle">Modelo de Precificação Mensal Recorrente</p>
          
          <div className="plans-grid">
            <div className="plan-card">
              <div className="plan-header small">
                <h3>Pequeno Porte</h3>
                <div className="plan-price">
                  <span className="price-label">A partir de</span>
                  <span className="price">R$ 2.000 - 3.000</span>
                </div>
              </div>
              <div className="plan-content">
                <h4>Perfil da Instalação</h4>
                <ul>
                  <li>Piscinas residenciais coletivas</li>
                  <li>Piscinas de pequenos hotéis</li>
                  <li>Clubes locais</li>
                </ul>
                <h4>Volume típico</h4>
                <p>Até 100m³</p>
              </div>
            </div>
            
            <div className="plan-card">
              <div className="plan-header medium">
                <h3>Médio Porte</h3>
                <div className="plan-price">
                  <span className="price-label">Entre</span>
                  <span className="price">R$ 3.000 - 10.000</span>
                </div>
              </div>
              <div className="plan-content">
                <h4>Perfil da Instalação</h4>
                <ul>
                  <li>Condomínios</li>
                  <li>Hotéis</li>
                  <li>Academias</li>
                  <li>Clubes recreativos</li>
                </ul>
                <h4>Volume típico</h4>
                <p>100m³ a 500m³</p>
              </div>
            </div>
            
            <div className="plan-card">
              <div className="plan-header large">
                <h3>Grande Porte</h3>
                <div className="plan-price">
                  <span className="price-label">Acima de</span>
                  <span className="price">R$ 10.000+</span>
                </div>
              </div>
              <div className="plan-content">
                <h4>Perfil da Instalação</h4>
                <ul>
                  <li>Grandes complexos aquáticos</li>
                  <li>Resorts</li>
                  <li>Parques aquáticos</li>
                  <li>Múltiplas piscinas</li>
                </ul>
                <h4>Volume típico</h4>
                <p>Acima de 500m³ ou múltiplas unidades</p>
              </div>
            </div>
          </div>
          
          <div className="plans-note">
            <h4>Inclui em todos os planos:</h4>
            <ul>
              <li>✓ Emissão de ART conforme Resolução 335/23</li>
              <li>✓ Responsabilidade Técnica Formal</li>
              <li>✓ Monitoramento Remoto 24/7</li>
              <li>✓ Gestão Técnica Contínua</li>
              <li>✓ Formação dos "Ninjas"</li>
              <li>✓ Transparência aos usuários</li>
              <li>✓ Relatórios técnicos automatizados</li>
              <li>✓ Suporte técnico</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Vamos Trabalhar e Cuidar da sua Piscina?</h2>
            <p>Garanta segurança jurídica, economia operacional e água saudável sempre!</p>
            <a href="https://api.whatsapp.com/send?phone=5511982922298" className="cta-button" target="_blank" rel="noopener noreferrer">
              Agende uma Avaliação
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ARTInteligente;